import { Framework } from '@/framework';
import { LayerType, type OpenPluginType } from '@/version-types';
import { getShortcutMenuState } from './states/shortcut-menu-state';
import type { IShortcutMenuItem, IShortcutMenuLayout, ShortcutMenuEmptySelectionRenderer } from './type';

/** 图层快捷菜单 */
export class ShortcutMenu extends Framework {
    private emptySelectionRendererRegistrationCount = new Map<ShortcutMenuEmptySelectionRenderer, number>();

    private getLayerMenuChangeEventName(layerType: LayerType | 'frame') {
        return `gs-shortcut-layer-menu-change-${layerType}`;
    }

    /** 设置指定图层类型的菜单布局 */
    setLayerMenu(layerType: LayerType | 'frame', menu: IShortcutMenuLayout) {
        getShortcutMenuState(this.identity).setLayerMenu(layerType, menu);
        this.framework.service.events.emit(this.getLayerMenuChangeEventName(layerType), menu);
        return this;
    }

    /** 监听指定图层类型的菜单布局；注册时立即回调当前值 */
    onLayerMenuChange(layerType: LayerType | 'frame', callback: (menu: IShortcutMenuLayout | undefined) => void) {
        const handler = this.framework.service.events.on(this.getLayerMenuChangeEventName(layerType), callback);
        callback(getShortcutMenuState(this.identity).layerMenus[layerType]);
        return () => this.framework.service.events.off(handler);
    }

    /** 设置快捷菜单是否可见 */
    setVisible(visible: boolean) {
        getShortcutMenuState(this.identity).setVisible(visible);
        return this;
    }

    /** 设置是否启用默认工具栏回退 */
    setDefaultMenuEnabled(enabled: boolean) {
        getShortcutMenuState(this.identity).setDefaultMenuEnabled(enabled);
        return this;
    }

    /** 设置是否显示高级 AI 操作 */
    setAdvancedAiEnabled(enabled: boolean) {
        getShortcutMenuState(this.identity).setAdvancedAiEnabled(enabled);
        return this;
    }

    /** 全量替换默认快捷工具栏项 */
    setItems(items: IShortcutMenuItem[]) {
        getShortcutMenuState(this.identity).setItems(items);
        return this;
    }

    /** 追加默认快捷工具栏项 */
    addItems(items: IShortcutMenuItem[]) {
        getShortcutMenuState(this.identity).addItems(items);
        return this;
    }

    /** 添加或更新业务自定义工具栏项 */
    addCustomItem(layerType: LayerType, key: string, node: React.ReactNode) {
        getShortcutMenuState(this.identity).addCustomItem({ layerType, key, node });
        return this;
    }

    /** 移除业务自定义工具栏项 */
    removeCustomItem(layerType: LayerType, key: string) {
        getShortcutMenuState(this.identity).removeCustomItem(layerType, key);
        return this;
    }

    /** 替换默认工具栏；传 null 恢复默认工具项 */
    replaceDefaultMenu(menu: React.ReactNode | null) {
        getShortcutMenuState(this.identity).setReplacementMenu(menu);
        return this;
    }

    /** 注册无激活图层场景的快捷栏扩展；同一组件允许被多个宿主重复注册 */
    registerEmptySelectionRenderer(renderer: ShortcutMenuEmptySelectionRenderer) {
        const count = this.emptySelectionRendererRegistrationCount.get(renderer) || 0;
        this.emptySelectionRendererRegistrationCount.set(renderer, count + 1);
        if (!count) {
            getShortcutMenuState(this.identity).addEmptySelectionRenderer(renderer);
        }

        let unregistered = false;
        return () => {
            if (unregistered) return;
            unregistered = true;

            const currentCount = this.emptySelectionRendererRegistrationCount.get(renderer);
            if (!currentCount) return;
            if (currentCount > 1) {
                this.emptySelectionRendererRegistrationCount.set(renderer, currentCount - 1);
                return;
            }

            this.emptySelectionRendererRegistrationCount.delete(renderer);
            getShortcutMenuState(this.identity).removeEmptySelectionRenderer(renderer);
        };
    }

    /** 监听快捷菜单事件 */
    onClick(callback: (func: string) => void, weight = 0) {
        return this.framework.service.events.on('gs-shortcut', callback, weight);
    }

    /** 触发快捷菜单事件 */
    emitClick(func: string) {
        this.framework.service.events.emit('gs-shortcut', func);
        return this;
    }

    /** 监听打开插件事件 */
    onOpenPlugin(callback: (plugin: OpenPluginType) => void) {
        const handler = this.framework.service.events.on('gs-shortcut-open-plugin', callback);
        return () => this.framework.service.events.off(handler);
    }

    /** 打开插件 */
    openPlugin(plugin: OpenPluginType) {
        this.framework.service.events.emit('gs-shortcut-open-plugin', plugin);
        return this;
    }

    /** 监听进入 AI 生图的入口事件 */
    onAiGenerateImageEnter(callback: (entrance: string) => void) {
        const handler = this.framework.service.events.on('gs-shortcut-ai-generate-image-enter', callback);
        return () => this.framework.service.events.off(handler);
    }

    /** 触发进入 AI 生图的入口事件 */
    emitAiGenerateImageEnter(entrance: string) {
        this.framework.service.events.emit('gs-shortcut-ai-generate-image-enter', entrance);
        return this;
    }
}
